import { bricolageGrotesque } from "../fonts/bricolage-grotesque.css";
import { inter } from "../fonts/inter.css";

import type { ThemeUnit } from "../../types";
import { pxToUnit } from "../utils";

// CSS default font size is 16px, so we can use that as a base for rem calculations
export const baseFontSize = 16;

export const fontFamily = {
  // Sans-serif (“without serifs”): fonts with clean lines, without these small
  // lines. Examples: Arial, Helvetica, Inter, Roboto. They have a modern and
  // minimalist look and are often preferred for web and mobile interfaces.
  sans: `${inter}, sans-serif`,

  // Serif: fonts with serifs (small decorative strokes at the ends of letters).
  // Examples: Times New Roman, Georgia, Garamond. They give a classic, formal
  // look and are traditionally considered more readable for long printed texts.
  serif: `serif`,

  // Only for headlines, where a face with more character than the body text is
  // worth the extra font to download.
  display: `${bricolageGrotesque}, ${inter}, sans-serif`,

  // Monospace (or “fixed-width”): each character occupies exactly the
  // same horizontal width. Examples: Courier, Fira Code, JetBrains Mono.
  // Indispensable for code because they allow perfect vertical alignment
  // and clearly distinguish similar characters (l, 1, I, 0, O).
  mono: `"Fira Code", "Courier New", monospace`,
};

const createFontSize = (unit: ThemeUnit) =>
  ({
    xxxxs: pxToUnit(6, unit),
    xxxs: pxToUnit(8, unit),
    xxs: pxToUnit(10, unit),
    xs: pxToUnit(12, unit),
    sm: pxToUnit(14, unit),
    md: pxToUnit(baseFontSize, unit),
    lg: pxToUnit(18, unit),
    xl: pxToUnit(20, unit),
    xxl: pxToUnit(24, unit),
    xxxl: pxToUnit(30, unit),
    xxxxl: pxToUnit(36, unit),
  }) as const;

export const fontSizeRem = createFontSize("rem");

export const fontWeight = {
  thin: "100",
  extralight: "200",
  light: "300",
  normal: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
  extrabold: "800",
  black: "900",
} as const;

export const lineHeight = {
  tight: "1.25",
  normal: "1.5",
  relaxed: "1.75",
} as const;
